import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';
import Select from '../../../ui/Select';
import { Checkbox } from '../../../ui/Checkbox';

const SearchFilters = ({ onFiltersChange, activeFilters = {} }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    searchQuery: activeFilters?.searchQuery || '',
    industry: activeFilters?.industry || '',
    stage: activeFilters?.stage || '',
    location: activeFilters?.location || '',
    fundingRange: activeFilters?.fundingRange || '',
    programType: activeFilters?.programType || '',
    verified: activeFilters?.verified || false,
    ...activeFilters
  });

  const industryOptions = [
    { value: '', label: 'All Industries' },
    { value: 'fintech', label: 'FinTech' },
    { value: 'healthtech', label: 'HealthTech' },
    { value: 'edtech', label: 'EdTech' },
    { value: 'cleantech', label: 'CleanTech' },
    { value: 'ai-ml', label: 'AI/ML' },
    { value: 'blockchain', label: 'Blockchain' },
    { value: 'iot', label: 'IoT' },
    { value: 'cybersecurity', label: 'Cybersecurity' },
    { value: 'retail', label: 'Retail Tech' },
    { value: 'logistics', label: 'Logistics' }
  ];

  const stageOptions = [
    { value: '', label: 'All Stages' },
    { value: 'pre-seed', label: 'Pre-Seed' },
    { value: 'seed', label: 'Seed' },
    { value: 'series-a', label: 'Series A' },
    { value: 'series-b', label: 'Series B' },
    { value: 'series-c', label: 'Series C+' },
    { value: 'growth', label: 'Growth Stage' }
  ];

  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia Pacific' },
    { value: 'latin-america', label: 'Latin America' },
    { value: 'middle-east', label: 'Middle East' },
    { value: 'africa', label: 'Africa' }
  ];

  const fundingRangeOptions = [
    { value: '', label: 'Any Funding' },
    { value: '0-100k', label: '$0 - $100K' },
    { value: '100k-500k', label: '$100K - $500K' },
    { value: '500k-1m', label: '$500K - $1M' },
    { value: '1m-5m', label: '$1M - $5M' },
    { value: '5m-10m', label: '$5M - $10M' },
    { value: '10m+', label: '$10M+' }
  ];

  const programTypeOptions = [
    { value: '', label: 'All Program Types' },
    { value: 'accelerator', label: 'Accelerator' },
    { value: 'incubator', label: 'Incubator' },
    { value: 'partnership', label: 'Strategic Partnership' },
    { value: 'pilot', label: 'Pilot Program' },
    { value: 'investment', label: 'Investment Opportunity' },
    { value: 'acquisition', label: 'Acquisition Interest' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      searchQuery: '',
      industry: '',
      stage: '',
      location: '',
      fundingRange: '',
      programType: '',
      verified: false
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => 
      value !== '' && value !== false
    )?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Search Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              iconName="X"
              iconPosition="left"
              iconSize={14}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            iconSize={16}
          >
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>
      {/* Search Query */}
      <div className="mb-4">
        <Input
          type="search"
          placeholder="Search programs, companies, or technologies..."
          value={filters?.searchQuery}
          onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Quick Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <Select
          label="Industry"
          options={industryOptions}
          value={filters?.industry}
          onChange={(value) => handleFilterChange('industry', value)}
          placeholder="Select industry"
        />
        
        <Select
          label="Stage"
          options={stageOptions}
          value={filters?.stage}
          onChange={(value) => handleFilterChange('stage', value)}
          placeholder="Select stage"
        />
        
        <Select
          label="Location"
          options={locationOptions}
          value={filters?.location}
          onChange={(value) => handleFilterChange('location', value)}
          placeholder="Select location"
        />
      </div>
      {/* Expanded Filters */}
      {isExpanded && (
        <div className="border-t border-border pt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Funding Range"
              options={fundingRangeOptions}
              value={filters?.fundingRange}
              onChange={(value) => handleFilterChange('fundingRange', value)}
              placeholder="Select funding range"
            />
            
            <Select
              label="Program Type"
              options={programTypeOptions}
              value={filters?.programType}
              onChange={(value) => handleFilterChange('programType', value)}
              placeholder="Select program type"
            />
          </div>

          <div className="flex items-center space-x-4">
            <Checkbox
              label="Verified startups only"
              checked={filters?.verified}
              onChange={(e) => handleFilterChange('verified', e?.target?.checked)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;